/**
 * Read-only admin finance dashboard API (server-side Stripe + Firestore aggregates).
 */
import { auth } from '../auth/firebaseAuth';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in with Firebase to view finance metrics.');
  return user.getIdToken();
}

/**
 * @param {{ lite?: boolean }} [opts]
 * @returns {Promise<Record<string, unknown>>}
 */
export async function fetchAdminFinanceReport({ lite = false } = {}) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const query = lite ? '?lite=1' : '';
  const res = await fetchWithTimeout(`${base}/admin_finance_report${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || `Finance report failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}
